import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import AddDirekterzeuger from './AddDirekterzeuger';
import DirecterzeugerMap from '../components/DirecterzeugerMap';
import haukeApiService from '../services/haukeApi';
import '../assets/css/profil-admin.css';

const UserSuggestionDetail = () => {
  const { suggestionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('suggestion');
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [mapDirekterzeuger, setMapDirekterzeuger] = useState([]);
  const [mapHasLoaded, setMapHasLoaded] = useState(false);

  const tabs = [
    { id: 'suggestion', label: 'Suggestion', icon: 'feedback' },
    { id: 'map', label: 'Karte', icon: 'map' },
    { id: 'create', label: 'Erstellen', icon: 'add_business' }
  ];

  useEffect(() => {
    if (suggestionId) {
      loadSuggestionDetails();
    }
  }, [suggestionId]);

  // Tab aus URL-Parameter setzen
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Beim Wechsel auf den Karten-Tab passende Direkterzeuger laden (nach PLZ/Stadt)
  useEffect(() => {
    const shouldLoadMap = activeTab === 'map' && suggestion && !mapHasLoaded && !mapLoading;
    if (!shouldLoadMap) return;

    const fetchMapDirekterzeuger = async () => {
      try {
        setMapLoading(true);
        setMapError(null);

        const response = await haukeApiService.getDirekterzeuger({
          page: 1,
          limit: 100,
          plz: suggestion?.plz || '',
          city: suggestion?.city || '',
          sortBy: 'name',
          sortOrder: 'asc'
        });

        const raw = response?.data || [];
        const normalized = raw
          .map((item) => ({
            id: item.hofId || item.id,
            name: item.name,
            city: item.city,
            plz: item.plz || item.postal_code,
            latitude: item.latitude,
            longitude: item.longitude,
            membership: item.membership
          }))
          .filter((h) => !!h.latitude && !!h.longitude);

        setMapDirekterzeuger(normalized);
        setMapHasLoaded(true);
      } catch (e) {
        console.error('Fehler beim Laden der Direkterzeuger für Karte:', e);
        setMapError(e?.message || 'Fehler beim Laden');
      } finally {
        setMapLoading(false);
      }
    };

    fetchMapDirekterzeuger();
  }, [activeTab, suggestion, mapHasLoaded, mapLoading]);

  const loadSuggestionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`https://europe-west3-lf1-app.cloudfunctions.net/userSuggestions/${suggestionId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setSuggestion(data.data);
      } else {
        setError('User Suggestion nicht gefunden');
      }
    } catch (error) {
      console.error('Error loading suggestion:', error);
      setError(`Fehler beim Laden der User Suggestion: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/user-suggestions');
  };

  const updateSuggestionStatus = async (newStatus, adminNotes = '') => {
    try {
      const response = await fetch(`https://europe-west3-lf1-app.cloudfunctions.net/userSuggestions/${suggestionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          adminNotes: adminNotes,
          processedAt: new Date().toISOString(),
          processedBy: user?.email || 'Unknown'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setSuggestion(prev => ({ 
          ...prev, 
          status: newStatus, 
          adminNotes, 
          processedAt: new Date().toISOString() 
        }));
        showSuccessToast(`Status erfolgreich auf "${getStatusLabel(newStatus)}" geändert`);
      }
    } catch (error) {
      console.error('Error updating suggestion:', error);
      showErrorToast('Fehler beim Aktualisieren des Status');
    }
  };

  const deleteSuggestion = async () => {
    if (!window.confirm('Möchten Sie diese User Suggestion wirklich löschen?')) {
      return;
    }

    try {
      const response = await fetch(`https://europe-west3-lf1-app.cloudfunctions.net/userSuggestions/${suggestionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      showSuccessToast('User Suggestion erfolgreich gelöscht');
      navigate('/user-suggestions');
    } catch (error) {
      console.error('Error deleting suggestion:', error);
      showErrorToast('Fehler beim Löschen der User Suggestion');
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'PENDING': 'Ausstehend',
      'APPROVED': 'Genehmigt',
      'REJECTED': 'Abgelehnt'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'PENDING': '#f59e0b',
      'APPROVED': '#10b981',
      'REJECTED': '#ef4444'
    };
    return colorMap[status] || '#6b7280';
  };

  const renderTabContent = () => {
    if (!suggestion) return null;

    switch (activeTab) {
      case 'suggestion':
        return renderSuggestionTab();
      case 'map':
        return renderMapTab();
      case 'create':
        return renderCreateTab();
      default:
        return renderSuggestionTab();
    }
  };

  const renderCreateTab = () => {
    // Übertrage die Suggestion-Daten als initialValues an AddDirekterzeuger
    const initialValues = {
      name: suggestion?.name || '',
      street: suggestion?.street || '',
      plz: suggestion?.plz || '',
      city: suggestion?.city || '',
      latitude: suggestion?.latitude || '',
      longitude: suggestion?.longitude || '',
      features: suggestion?.features || []
    };

    return (
      <div className="tab-panel">
        <AddDirekterzeuger initialValues={initialValues} />
      </div>
    );
  };

  const renderSuggestionTab = () => {
    return (
      <div className="tab-panel">
        <div className="overview-grid">
          {/* Grunddaten */}
          <div className="overview-card">
            <div className="card-header">
              <h3>Grunddaten</h3>
              <i className="material-icons">info</i>
            </div>
            <div className="card-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>Name</label>
                  <span>{suggestion?.name || 'Nicht angegeben'}</span>
                </div>
                <div className="info-item">
                  <label>Straße</label>
                  <span>{suggestion?.street || 'Nicht angegeben'}</span>
                </div>
                <div className="info-item">
                  <label>PLZ</label>
                  <span>{suggestion?.plz || 'Nicht angegeben'}</span>
                </div>
                <div className="info-item">
                  <label>Stadt</label>
                  <span>{suggestion?.city || 'Nicht angegeben'}</span>
                </div>
                <div className="info-item">
                  <label>Koordinaten</label>
                  <span>
                    {suggestion?.latitude && suggestion?.longitude ? (
                      <a 
                        href={`https://maps.google.com/?q=${suggestion.latitude},${suggestion.longitude}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {suggestion.latitude}, {suggestion.longitude}
                      </a>
                    ) : 'Nicht verfügbar'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Geohash</label>
                  <span>{suggestion?.geohash || 'Nicht verfügbar'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Benutzer Info */}
          <div className="overview-card">
            <div className="card-header">
              <h3>Benutzer</h3>
              <i className="material-icons">person</i>
            </div>
            <div className="card-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>Name</label>
                  <span>{suggestion?.userName || 'Unbekannt'}</span>
                </div>
                <div className="info-item">
                  <label>User ID</label>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {suggestion?.userId || 'Nicht verfügbar'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Erstellt am</label>
                  <span>{formatDate(suggestion?.createdAt)}</span>
                </div>
                <div className="info-item">
                  <label>Status</label>
                  <span 
                    className="membership-badge"
                    style={{ backgroundColor: getStatusColor(suggestion?.status) }}
                  >
                    {getStatusLabel(suggestion?.status)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        {suggestion?.features && suggestion.features.length > 0 && (
          <div className="section-card">
            <div className="card-header">
              <h3>Features ({suggestion.features.length})</h3>
              <i className="material-icons">build</i>
            </div>
            <div className="card-content">
              <div className="categories-list">
                {suggestion.features.map((feature, index) => (
                  <span key={index} className="category-tag">
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bilder */}
        {suggestion?.imageUrls && suggestion.imageUrls.length > 0 && (
          <div className="section-card">
            <div className="card-header">
              <h3>Bilder ({suggestion.imageUrls.length})</h3>
              <i className="material-icons">photo_library</i>
            </div>
            <div className="card-content">
              <div className="images-grid">
                {suggestion.imageUrls.map((url, index) => (
                  <div key={index} className="image-item">
                    <img 
                      src={url} 
                      alt={`Bild ${index + 1}`}
                      onClick={() => window.open(url, '_blank')}
                      style={{ 
                        width: '100%', 
                        height: '200px', 
                        objectFit: 'cover', 
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: '1px solid #e2e8f0'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Admin Aktionen */}
        <div className="section-card">
          <div className="card-header">
            <h3>Admin Aktionen</h3>
            <i className="material-icons">admin_panel_settings</i>
          </div>
          <div className="card-content">
            <div className="action-buttons" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {suggestion?.status === 'PENDING' && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => updateSuggestionStatus('APPROVED')}
                  >
                    <i className="material-icons">check</i>
                    Genehmigen
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => updateSuggestionStatus('REJECTED')}
                  >
                    <i className="material-icons">close</i>
                    Ablehnen
                  </button>
                </>
              )}
              <button
                className="btn btn-danger btn-outline"
                onClick={deleteSuggestion}
              >
                <i className="material-icons">delete</i>
                Löschen
              </button>
            </div>
            
            {suggestion?.adminNotes && (
              <div style={{ marginTop: '20px' }}>
                <label style={{ fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
                  Admin Notizen:
                </label>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0, padding: '12px', background: '#f8fafc', borderRadius: '6px' }}>
                  {suggestion.adminNotes}
                </p>
              </div>
            )}

            {suggestion?.processedAt && (
              <div style={{ marginTop: '16px', fontSize: '12px', color: '#9ca3af' }}>
                Bearbeitet am: {formatDate(suggestion.processedAt)} 
                {suggestion.processedBy && ` von ${suggestion.processedBy}`}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderMapTab = () => {
    return (
      <div className="tab-panel">
        <div style={{ padding: '8px 12px', background: '#F0F7FF', borderBottom: '1px solid #E8ECF0', fontSize: '13px', color: '#1565C0' }}>
          Hinweis: Es werden bestehende Einträge aus derselben Stadt/PLZ angezeigt.
        </div>
        {mapLoading && (
          <div className="loading-state">
            <i className="material-icons">hourglass_empty</i>
            <p>Lade Karte...</p>
          </div>
        )}
        {mapError && (
          <div className="error-state">
            <i className="material-icons">error</i>
            <p>{mapError}</p>
          </div>
        )}
        {!mapLoading && !mapError && mapDirekterzeuger.length === 0 && (
          <div className="error-state">
            <i className="material-icons">location_off</i>
            <p>Keine bestehenden Einträge mit Koordinaten gefunden.</p>
          </div>
        )}
        {!mapLoading && !mapError && mapDirekterzeuger.length > 0 && (
          <div className="map-wrapper">
            <DirecterzeugerMap 
              direkterzeuger={mapDirekterzeuger}
              onMarkerClick={(hofId) => navigate(`/profil-admin/${hofId}`)}
              center={[
                Number(suggestion?.latitude) || 51.4556,
                Number(suggestion?.longitude) || 7.0116
              ]}
              zoom={14}
              highlight={{
                latitude: Number(suggestion?.latitude),
                longitude: Number(suggestion?.longitude)
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="profil-admin">
        <div className="loading-state">
          <i className="material-icons">hourglass_empty</i>
          <p>Lade User Suggestion...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profil-admin">
        <div className="error-state">
          <i className="material-icons">error</i>
          <p>{error}</p>
          <button className="btn btn-outline" onClick={handleGoBack}>
            <i className="material-icons">arrow_back</i>
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profil-admin">
      {/* Header */}
      <div className="profil-header">
        <div className="header-content">
          <button className="back-button" onClick={handleGoBack}>
            <i className="material-icons">arrow_back</i>
            Zurück zur Übersicht
          </button>
          <div className="header-info">
            <h1>{suggestion?.name || 'User Suggestion'}</h1>
            <div className="header-meta">
              <span className="hof-id">ID: {suggestionId}</span>
              <span className="location">
                <i className="material-icons">location_on</i>
                {suggestion?.city}, {suggestion?.plz}
              </span>
              <span 
                className="membership-badge"
                style={{ backgroundColor: getStatusColor(suggestion?.status) }}
              >
                {getStatusLabel(suggestion?.status)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tab-container">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className="material-icons">{tab.icon}</i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default UserSuggestionDetail;
